import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { App as CapApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import apiService from '../services/apiService';
import { isNative } from '../utils/platform.js';
import { installLocalSessionFetch } from '../config/localSessionFetch.js';

const AUTH0_DEFAULT_SCOPES = ['openid', 'profile', 'email', 'offline_access'];
const AUTH0_AUDIENCE = import.meta.env.VITE_AUTH0_AUDIENCE || '';
const BACKEND_VERIFY_MAX_WAIT_MS = 90000;
const BACKEND_VERIFY_INITIAL_RETRY_DELAY_MS = 1000;
const BACKEND_VERIFY_MAX_RETRY_DELAY_MS = 8000;
// Re-verify session when tab returns after being hidden for at least 5 minutes.
// Matches TAB_SUSPEND_RECONNECT_MS in useGameSocket.js — a brief tab-away leaves
// both the WebSocket transport and the auth session alive, so we don't unmount
// the campaign UI behind a loading spinner just because the user glanced away.
const SESSION_REVALIDATION_THRESHOLD_MS = 5 * 60 * 1000;

const AuthContext = createContext(null);

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isRetryableBackendVerificationError = (error) => {
  const status = Number(error?.status);
  if (Number.isFinite(status)) {
    return status >= 500 || status === 429;
  }

  const message = `${error?.message || ''} ${error?.error_description || ''}`.toLowerCase();
  const name = (error?.name || '').toLowerCase();
  return (
    error?.code === 'BACKEND_VERIFY_TIMEOUT' ||
    name === 'backendverificationtimeouterror' ||
    name === 'aborterror' ||
    message.includes('network') ||
    message.includes('fetch') ||
    message.includes('timeout') ||
    message.includes('failed to fetch')
  );
};

const ensureAuthOptions = (options = {}) => {
  const merged = { ...options };
  const authParams = { ...(options.authorizationParams || {}) };

  if (AUTH0_AUDIENCE && !authParams.audience) {
    authParams.audience = AUTH0_AUDIENCE;
  }

  const currentScopes = new Set(
    (authParams.scope || '')
      .split(' ')
      .map((scope) => scope.trim())
      .filter(Boolean)
  );
  AUTH0_DEFAULT_SCOPES.forEach((scope) => currentScopes.add(scope));
  authParams.scope = Array.from(currentScopes).join(' ');

  merged.authorizationParams = authParams;
  return merged;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export function Auth0AuthProvider({ children }) {
  const {
    isAuthenticated,
    isLoading,
    user: auth0User,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently: auth0GetAccessTokenSilently,
    handleRedirectCallback
  } = useAuth0();

  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revalidationTrigger, setRevalidationTrigger] = useState(0);
  const hiddenAtRef = useRef(null);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  // Re-verify session when tab returns after being hidden for a while.
  // Without this, returning to an already-loaded page shows no loading indicator
  // even though the backend may be cold and the WebSocket is dead.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        hiddenAtRef.current = Date.now();
      } else if (hiddenAtRef.current) {
        const hiddenMs = Date.now() - hiddenAtRef.current;
        hiddenAtRef.current = null;
        if (hiddenMs >= SESSION_REVALIDATION_THRESHOLD_MS && isAuthenticated && user) {
          console.log(`[Auth] Tab returned after ${Math.round(hiddenMs / 1000)}s — revalidating session`);
          setRevalidationTrigger(prev => prev + 1);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated, user]);

  // Listen for deep link callbacks from Auth0 login (native only).
  // When Chrome Custom Tabs redirects to com.fabletable.tavern://...,
  // Android fires appUrlOpen. We complete the token exchange and close the browser.
  useEffect(() => {
    if (!isNative()) return;
    const listener = CapApp.addListener('appUrlOpen', async ({ url }) => {
      if (url.includes('state') && (url.includes('code') || url.includes('error'))) {
        try {
          await handleRedirectCallback(url);
        } catch (err) {
          console.error('[Auth] handleRedirectCallback failed:', err);
        }
        try {
          await Browser.close();
        } catch {
          // Browser may already be closed
        }
      }
    });
    return () => { listener.then(l => l.remove()); };
  }, [handleRedirectCallback]);

  // Sync user data when Auth0 authentication changes
  useEffect(() => {
    let cancelled = false;

    const syncUserData = async () => {
      if (isAuthenticated && auth0User) {
        setLoading(true);
        try {
          // Get the access token for API calls (with 10s timeout to avoid hanging on cold starts)
          const token = await Promise.race([
            auth0GetAccessTokenSilently(ensureAuthOptions()),
            new Promise((_, reject) =>
              setTimeout(() => reject({ error: 'login_required', message: 'Token acquisition timed out' }), 10000)
            ),
          ]);
          if (cancelled) {
            return;
          }
          setAccessToken(token);

          // Verify token with backend. Retry transient backend startup/network errors so
          // users are not forced through repeated login loops during cold starts.
          const verificationStartedAt = Date.now();
          let verifyAttempt = 0;

          while (!cancelled) {
            try {
              const userData = await apiService.verifyAuth0Token(token);
              if (cancelled) {
                return;
              }

              // Merge GAIA user data with Auth0 sub
              // Note: Backend uses GAIA user_id (UUID) for audio queues, not auth0_sub
              setUser({
                ...userData,
                auth0_sub: auth0User.sub  // Auth0 subject ID (kept for reference/debugging)
              });
              break;
            } catch (error) {
              if (isRetryableBackendVerificationError(error)) {
                const elapsedMs = Date.now() - verificationStartedAt;
                if (elapsedMs >= BACKEND_VERIFY_MAX_WAIT_MS) {
                  if (!cancelled) {
                    const message = encodeURIComponent('The server is still starting. Please wait a moment and retry.');
                    navigateRef.current(`/auth-error?reason=backend_unavailable&message=${message}`);
                    setUser(null);
                    setAccessToken(null);
                    setLoading(false);
                  }
                  return;
                }

                const retryDelayMs = Math.min(
                  BACKEND_VERIFY_INITIAL_RETRY_DELAY_MS * (2 ** verifyAttempt),
                  BACKEND_VERIFY_MAX_RETRY_DELAY_MS
                );
                verifyAttempt += 1;
                console.warn(
                  `[Auth] Backend verify failed with a retryable error (${error?.message || error?.name || 'unknown error'}). Retrying in ${retryDelayMs}ms (attempt ${verifyAttempt}).`
                );
                await wait(retryDelayMs);
                continue;
              }

              console.error('Failed to verify user with backend:', error);

              // Non-retryable auth/authorization failures
              const status = error?.status;
              const errorMsg = error?.message || error?.error_description || 'Unknown error';
              const encodedMsg = encodeURIComponent(errorMsg);
              const emailVerificationRequired = error?.headers?.get?.('X-Email-Verification-Required') === 'true'
                || errorMsg.toLowerCase().includes('email not verified');

              if (emailVerificationRequired) {
                navigateRef.current(`/auth-error?reason=email_not_verified&message=${encodedMsg}`);
              } else if (status === 403) {
                navigateRef.current(`/auth-error?reason=not_authorized&message=${encodedMsg}`);
              } else if (status === 401) {
                navigateRef.current(`/auth-error?reason=invalid_token&message=${encodedMsg}`);
              } else {
                navigateRef.current(`/auth-error?reason=oauth_error&message=${encodedMsg}`);
              }
              // Mark as unauthorized locally and stop loading
              if (!cancelled) {
                setUser(null);
                setAccessToken(null);
                setLoading(false);
              }
              return;
            }
          }

          if (cancelled) {
            return;
          }
        } catch (error) {
          console.error('Error getting access token:', error);
          const errorMessage = `${error?.error || ''} ${error?.error_description || error?.message || ''}`.trim();

          // Check if this is a "login_required" or "invalid_grant" error - this means
          // the session/refresh token is expired and needs a new login, not an error page
          if (error?.error === 'login_required' || error?.message?.includes('login_required') ||
              error?.error === 'invalid_grant' || errorMessage.includes('invalid_grant')) {
            console.log('Session expired or refresh token invalid, will trigger login on next protected route access');
            if (!cancelled) {
              setUser(null);
              setAccessToken(null);
              setLoading(false);
            }
            return;
          }
          // Check for consent_required - also needs re-authentication
          if (error?.error === 'consent_required' || error?.message?.includes('consent_required')) {
            console.log('Consent required, will trigger login on next protected route access');
            if (!cancelled) {
              setUser(null);
              setAccessToken(null);
              setLoading(false);
            }
            return;
          }
          // Check for missing refresh token - need to re-authenticate
          const missingRefreshToken =
            error?.error === 'missing_refresh_token' ||
            /missing refresh token/i.test(errorMessage);
          if (missingRefreshToken) {
            console.warn('🔐 Missing refresh token, clearing auth state. User must re-authenticate.');
            if (!cancelled) {
              setUser(null);
              setAccessToken(null);
              setLoading(false);
            }
            return;
          }
          // For other errors, redirect to auth error page
          const encodedNetworkMsg = encodeURIComponent(errorMessage || 'Network error occurred');
          if (!cancelled) {
            navigateRef.current(`/auth-error?reason=network_error&message=${encodedNetworkMsg}`);
            setUser(null);
            setAccessToken(null);
            setLoading(false);
          }
          return;
        }
      } else {
        if (!cancelled) {
          setUser(null);
          setAccessToken(null);
        }
      }
      if (!cancelled) {
        setLoading(false);
      }
    };

    if (!isLoading) {
      syncUserData();
    }

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isLoading, auth0User, auth0GetAccessTokenSilently, revalidationTrigger]);

  // Auto-trigger login on native apps when not authenticated.
  // On a fresh install there's no cached session, so we go straight to Auth0.
  const autoLoginTriggered = useRef(false);
  useEffect(() => {
    if (!isNative() || isLoading || isAuthenticated || autoLoginTriggered.current) return;
    autoLoginTriggered.current = true;
    console.log('[Auth] Native auto-login: no cached session, redirecting to Auth0');
    const merged = ensureAuthOptions({});
    merged.openUrl = async (url) => {
      await Browser.open({ url, windowName: '_self' });
    };
    loginWithRedirect(merged);
  }, [isLoading, isAuthenticated, loginWithRedirect]);

  const login = useCallback((options = {}) => {
    const merged = ensureAuthOptions(options);
    if (isNative()) {
      merged.openUrl = async (url) => {
        await Browser.open({ url, windowName: '_self' });
      };
    }
    loginWithRedirect(merged);
  }, [loginWithRedirect]);

  const logout = useCallback(() => {
    // Clear any local state
    setUser(null);
    setAccessToken(null);

    // Logout from Auth0
    const logoutOpts = {
      logoutParams: {
        returnTo: isNative()
          ? 'com.fabletable.tavern://callback'
          : import.meta.env.VITE_AUTH0_LOGOUT_URL || window.location.origin
      }
    };
    if (isNative()) {
      logoutOpts.openUrl = async (url) => {
        await Browser.open({ url, windowName: '_self' });
      };
    }
    auth0Logout(logoutOpts);
  }, [auth0Logout]);

  const refreshAccessToken = useCallback(async () => {
    try {
      const token = await auth0GetAccessTokenSilently(
        ensureAuthOptions({
          cacheMode: 'off' // Force token refresh
        })
      );
      setAccessToken(token);
      return token;
    } catch (error) {
      const errorMessage = `${error?.error || ''} ${error?.error_description || error?.message || ''}`.trim();
      const missingRefreshToken =
        error?.error === 'missing_refresh_token' ||
        /missing refresh token/i.test(errorMessage);

      const invalidGrant =
        error?.error === 'invalid_grant' ||
        /invalid_grant/i.test(error?.message || '');

      if (missingRefreshToken || invalidGrant) {
        console.warn('No refresh token or stale grant; prompting user to reauthenticate');
        await loginWithRedirect(
          ensureAuthOptions({
            authorizationParams: {
              prompt: 'login'
            }
          })
        );
        return null;
      }

      console.error('Failed to refresh token:', error);
      throw error;
    }
  }, [auth0GetAccessTokenSilently, loginWithRedirect]);

  const getAccessTokenSilently = useCallback(
    async (options = {}) => auth0GetAccessTokenSilently(ensureAuthOptions(options)),
    [auth0GetAccessTokenSilently]
  );

  // Handle authentication errors (expired tokens, etc.)
  const handleAuthError = useCallback((error) => {
    console.error('🔐 Authentication error detected, logging out:', error);
    logout();
  }, [logout]);

  // True when re-verifying after tab return (loading is true but user was already set)
  const sessionRevalidating = Boolean(loading && user !== null && revalidationTrigger > 0);

  const value = {
    user,
    accessToken,
    loading: loading || isLoading,
    // Consider authenticated only if Auth0 says so AND we have a verified user
    // This prevents the case where Auth0 has cached tokens but we failed to verify
    isAuthenticated: isAuthenticated && user !== null,
    // Also expose raw Auth0 state for cases where we need to know about cached session
    hasAuth0Session: isAuthenticated,
    authVerificationPending: Boolean(isAuthenticated && (loading || isLoading) && user === null),
    sessionRevalidating,
    login,
    logout,
    refreshAccessToken,
    getAccessTokenSilently,
    handleAuthError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// The desktop shell's preload (Electron) exposes the per-launch Local Player
// session secret as window.gaiaDesktop.sessionToken.
const readDesktopSessionToken = () => {
  const token = typeof window !== 'undefined' ? window.gaiaDesktop?.sessionToken : null;
  return typeof token === 'string' && token.trim() ? token : null;
};

const LOCAL_SESSION_MISSING_MESSAGE =
  'Gaia must be launched from the Gaia desktop app. This page has no Local Player session.';

function LocalSessionUnavailable({ message }) {
  return (
    <div
      role="alert"
      className="min-h-screen bg-gaia-bg flex items-center justify-center p-4"
    >
      <div className="max-w-lg w-full p-8 text-center">
        <h1 className="text-3xl font-bold text-white mb-4">Launch Gaia from the desktop app</h1>
        <p className="text-gaia-muted leading-relaxed">{message}</p>
      </div>
    </div>
  );
}

// Standalone Install provider: the Local Player is authenticated by the desktop
// shell's per-launch session secret, not an identity provider. Supplies that
// secret wherever Auth0AuthProvider supplies an access token, so apiService,
// sockets and ?token= audio URLs work unchanged. `user` is the backend's Local
// Player record (same /api/auth0/verify payload as hosted).
export function LocalAuthProvider({ children }) {
  const [sessionToken] = useState(() => {
    const token = readDesktopSessionToken();
    // Before any child renders: direct fetch() calls need the token too.
    installLocalSessionFetch(token);
    return token;
  });
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(Boolean(sessionToken));
  const [sessionError, setSessionError] = useState(
    sessionToken ? null : LOCAL_SESSION_MISSING_MESSAGE
  );

  useEffect(() => {
    if (!sessionToken) {
      return undefined;
    }
    let cancelled = false;

    const verifyLocalPlayer = async () => {
      const verificationStartedAt = Date.now();
      let verifyAttempt = 0;
      while (!cancelled) {
        try {
          const userData = await apiService.verifyAuth0Token(sessionToken);
          if (!cancelled) {
            setUser(userData);
            setLoading(false);
          }
          return;
        } catch (error) {
          const elapsedMs = Date.now() - verificationStartedAt;
          if (isRetryableBackendVerificationError(error) && elapsedMs < BACKEND_VERIFY_MAX_WAIT_MS) {
            const retryDelayMs = Math.min(
              BACKEND_VERIFY_INITIAL_RETRY_DELAY_MS * (2 ** verifyAttempt),
              BACKEND_VERIFY_MAX_RETRY_DELAY_MS
            );
            verifyAttempt += 1;
            await wait(retryDelayMs);
            continue;
          }
          console.error('[Auth] Local Player session was not accepted by the backend:', error);
          if (!cancelled) {
            setSessionError(
              `The local game server did not accept this session (${error?.message || 'unknown error'}). Restart Gaia from the desktop app.`
            );
            setLoading(false);
          }
          return;
        }
      }
    };

    verifyLocalPlayer();
    return () => {
      cancelled = true;
    };
  }, [sessionToken]);

  const getAccessTokenSilently = useCallback(async () => sessionToken, [sessionToken]);

  const handleAuthError = useCallback((error) => {
    console.error('🔐 Local Player session rejected:', error);
    setSessionError('The local game server rejected this session. Restart Gaia from the desktop app.');
  }, []);

  if (sessionError) {
    return <LocalSessionUnavailable message={sessionError} />;
  }

  const value = {
    user,
    accessToken: sessionToken,
    loading,
    isAuthenticated: user !== null,
    hasAuth0Session: false,
    authVerificationPending: loading,
    sessionRevalidating: false,
    // There is no sign-in or sign-out: the shell owns the session.
    login: () => {},
    logout: () => {},
    refreshAccessToken: getAccessTokenSilently,
    getAccessTokenSilently,
    handleAuthError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Dev/bypass provider used when Auth0 is not required or not configured
export function DevAuthProvider({ children }) {
  const value = {
    user: null,
    accessToken: null,
    loading: false,
    isAuthenticated: false,
    hasAuth0Session: false,
    authVerificationPending: false,
    sessionRevalidating: false,
    login: () => {},
    logout: () => {},
    refreshAccessToken: async () => null,
    getAccessTokenSilently: async () => null,
    handleAuthError: () => {}
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
